import { useState, useEffect } from "react";
import { Workflow, Play, Pause, Trash2, Clock, CheckCircle, AlertCircle, AlertTriangle, DollarSign, Calendar, Users, Mail } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";
import { format } from "date-fns";
import { DeployWorkflowAgentDialog } from "@/components/chaser/DeployWorkflowAgentDialog";

interface WorkflowAgentData {
  id: string;
  name: string;
  type: string;
  email_alias: string;
  status: string;
  created_at: string;
  parties: unknown;
}

interface WorkflowInstanceData {
  id: string;
  type: string;
  status: string;
  fund_name: string | null;
  amount: number | null;
  due_date: string | null;
  source_email_from: string | null;
  source_email_subject: string | null;
  extraction_confidence: number | null;
  wire_warning: {
    message: string;
    changes: Array<{ field: string; previous: unknown; current: unknown }>;
    previous_instructions: Record<string, unknown>;
    current_instructions: Record<string, unknown>;
  } | null;
  party_statuses: Array<{
    role: string;
    name: string;
    email: string;
    status: string;
    notified_at: string | null;
  }>;
  created_at: string;
  notified_at: string | null;
  workflow_agents: {
    name: string;
  } | null;
}

export default function Workflows() {
  const { orgId } = useTenant();
  const [agents, setAgents] = useState<WorkflowAgentData[]>([]);
  const [instances, setInstances] = useState<WorkflowInstanceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState<WorkflowInstanceData | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [orgId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch agents
      const { data: agentData, error: agentError } = await supabase
        .from("workflow_agents")
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (agentError) throw agentError;
      setAgents(agentData || []);

      // Fetch active instances
      const { data: instanceData, error: instanceError } = await supabase
        .from("workflow_instances")
        .select(`
          *,
          workflow_agents (name)
        `)
        .eq("org_id", orgId)
        .not("status", "in", '("completed","cancelled")')
        .order("created_at", { ascending: false });

      if (instanceError) throw instanceError;
      setInstances(instanceData || []);
    } catch (err) {
      toast.error("Failed to fetch data: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = fetchData;

  const handleToggleAgent = async (agentId: string, currentStatus: string) => {
    const newStatus = currentStatus === "active" ? "paused" : "active";
    try {
      const { error } = await supabase
        .from("workflow_agents")
        .update({ status: newStatus })
        .eq("id", agentId);

      if (error) throw error;
      toast.success(`Agent ${newStatus === "active" ? "activated" : "paused"}`);
      fetchAgents();
    } catch (err) {
      toast.error("Failed to update agent: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const handleDeleteAgent = async (agentId: string) => {
    if (!confirm("Delete this agent? This cannot be undone.")) return;
    
    try {
      const { error } = await supabase
        .from("workflow_agents")
        .delete()
        .eq("id", agentId);

      if (error) throw error;
      toast.success("Agent deleted");
      fetchAgents();
    } catch (err) {
      toast.error("Failed to delete agent: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const handleUpdateInstanceStatus = async (instanceId: string, newStatus: string) => {
    try {
      const updates: Record<string, unknown> = { status: newStatus };
      if (newStatus === "completed") {
        updates.completed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from("workflow_instances")
        .update(updates)
        .eq("id", instanceId);

      if (error) throw error;
      toast.success(`Workflow marked as ${newStatus}`);
      fetchData();
      setDetailsDialogOpen(false);
    } catch (err) {
      toast.error("Failed to update: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const getInstanceStatusBadge = (status: string) => {
    const config: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof Clock; label: string }> = {
      pending: { variant: "secondary", icon: Clock, label: "Pending" },
      extracted: { variant: "outline", icon: CheckCircle, label: "Data Extracted" },
      notified: { variant: "default", icon: Mail, label: "Parties Notified" },
      awaiting_funding: { variant: "secondary", icon: DollarSign, label: "Awaiting Funding" },
      completed: { variant: "outline", icon: CheckCircle, label: "Completed" },
      cancelled: { variant: "destructive", icon: AlertCircle, label: "Cancelled" },
    };
    const { variant, icon: Icon, label } = config[status] || config.pending;
    return (
      <Badge variant={variant}>
        <Icon className="mr-1 h-3 w-3" />
        {label}
      </Badge>
    );
  };

  const counters = {
    totalAgents: agents.length,
    activeAgents: agents.filter(a => a.status === "active").length,
    activeInstances: instances.length,
    awaitingFunding: instances.filter(i => i.status === "awaiting_funding").length,
  };

  if (loading) {
    return <div className="flex-1 p-8">Loading...</div>;
  }

  return (
    <div className="flex-1 space-y-6 px-6 py-6">
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Workflow Agents</h1>
          <p className="text-muted-foreground">
            Orchestration agents that automate multi-party workflows like capital calls and distributions
          </p>
        </div>
        <Button onClick={() => setDeployDialogOpen(true)}>
          <Workflow className="h-4 w-4" />
          Deploy Workflow Agent
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Active Agents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{counters.activeAgents}</div>
            <p className="text-xs text-muted-foreground">{counters.totalAgents} total</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Active Workflows</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{counters.activeInstances}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Awaiting Funding</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{counters.awaitingFunding}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {instances.filter(i => {
                const created = new Date(i.created_at);
                const now = new Date();
                return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
              }).length}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="instances" className="space-y-4">
        <TabsList>
          <TabsTrigger value="instances">Active Workflows</TabsTrigger>
          <TabsTrigger value="agents">Deployed Agents</TabsTrigger>
        </TabsList>

        <TabsContent value="instances" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Active Workflow Instances</CardTitle>
              <CardDescription>
                Capital calls and distributions being processed
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fund</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Parties</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {instances.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                        No active workflows. Capital call emails will appear here when received.
                      </TableCell>
                    </TableRow>
                  ) : (
                    instances.map((instance) => {
                      const notifiedCount = instance.party_statuses?.filter(p => p.status === "notified").length || 0;
                      const totalParties = instance.party_statuses?.length || 0;
                      return (
                        <TableRow key={instance.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <DollarSign className="h-4 w-4 text-muted-foreground" />
                              {instance.fund_name || "Unknown Fund"}
                              {instance.wire_warning && (
                                <Badge variant="destructive" className="ml-1 text-xs">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Wire Changed
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              via {instance.workflow_agents?.name || "—"}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {instance.type.replace("_", " ").toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono">
                            {instance.amount
                              ? `$${instance.amount.toLocaleString()}`
                              : "—"}
                          </TableCell>
                          <TableCell>
                            {instance.due_date
                              ? format(new Date(instance.due_date), "MMM d, yyyy")
                              : "—"}
                          </TableCell>
                          <TableCell>
                            {getInstanceStatusBadge(instance.status)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3 text-muted-foreground" />
                              <span className="text-sm">
                                {notifiedCount}/{totalParties}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {format(new Date(instance.created_at), "MMM d, h:mm a")}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedInstance(instance);
                                  setDetailsDialogOpen(true);
                                }}
                              >
                                View
                              </Button>
                              {instance.status === "notified" && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={() => handleUpdateInstanceStatus(instance.id, "awaiting_funding")}
                                >
                                  Mark Funding
                                </Button>
                              )}
                              {instance.status === "awaiting_funding" && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={() => handleUpdateInstanceStatus(instance.id, "completed")}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Complete
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="agents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Deployed Workflow Agents</CardTitle>
              <CardDescription>
                Agents that orchestrate complex multi-party workflows
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Email Alias</TableHead>
                <TableHead>Parties</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Deployed</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No workflow agents deployed yet. Click "Deploy Workflow Agent" above to get started.
                  </TableCell>
                </TableRow>
              ) : (
                agents.map((agent) => {
                  const partiesCount = Array.isArray(agent.parties) ? agent.parties.length : 0;
                  return (
                    <TableRow key={agent.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Workflow className="h-4 w-4 text-muted-foreground" />
                          {agent.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {agent.type.replace("_", " ").toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {agent.email_alias}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {partiesCount} contact{partiesCount !== 1 ? "s" : ""}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={agent.status === "active" ? "default" : "secondary"}>
                          {agent.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {format(new Date(agent.created_at), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleToggleAgent(agent.id, agent.status)}
                          >
                            {agent.status === "active" ? (
                              <Pause className="h-4 w-4" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteAgent(agent.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <DeployWorkflowAgentDialog
        open={deployDialogOpen}
        onOpenChange={setDeployDialogOpen}
        onSuccess={fetchData}
      />

      {/* Instance Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Workflow Details</DialogTitle>
            <DialogDescription>
              {selectedInstance?.fund_name || "Capital Call"} - {selectedInstance?.type.replace("_", " ")}
            </DialogDescription>
          </DialogHeader>
          {selectedInstance && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Amount</div>
                  <div className="text-lg font-mono">
                    {selectedInstance.amount
                      ? `$${selectedInstance.amount.toLocaleString()}`
                      : "Not extracted"}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Due Date</div>
                  <div className="text-lg">
                    {selectedInstance.due_date
                      ? format(new Date(selectedInstance.due_date), "MMMM d, yyyy")
                      : "Not extracted"}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Status</div>
                  <div className="mt-1">{getInstanceStatusBadge(selectedInstance.status)}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Confidence</div>
                  <div className="text-lg">
                    {selectedInstance.extraction_confidence
                      ? `${(selectedInstance.extraction_confidence * 100).toFixed(0)}%`
                      : "—"}
                  </div>
                </div>
              </div>

              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">Source Email</div>
                <div className="rounded-md border p-3 bg-muted/50">
                  <div className="text-sm">
                    <span className="font-medium">From:</span> {selectedInstance.source_email_from || "—"}
                  </div>
                  <div className="text-sm mt-1">
                    <span className="font-medium">Subject:</span> {selectedInstance.source_email_subject || "—"}
                  </div>
                </div>
              </div>

              {selectedInstance.wire_warning && (
                <div className="rounded-md border border-destructive bg-destructive/5 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    <span className="text-sm font-medium text-destructive">Wire Instruction Change Detected</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{selectedInstance.wire_warning.message}</p>
                  <div className="space-y-2">
                    {selectedInstance.wire_warning.changes.map((change, idx) => (
                      <div key={idx} className="grid grid-cols-3 gap-2 text-sm rounded border p-2 bg-background">
                        <div className="font-medium capitalize">{change.field.replace(/_/g, " ")}</div>
                        <div className="text-red-600 line-through">{String(change.previous)}</div>
                        <div className="text-green-600 font-medium">{String(change.current)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">Party Status</div>
                <div className="space-y-2">
                  {selectedInstance.party_statuses?.map((party, idx) => (
                    <div key={idx} className="flex items-center justify-between rounded-md border p-3">
                      <div>
                        <div className="font-medium">{party.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {party.role.replace("_", " ")} • {party.email}
                        </div>
                      </div>
                      <Badge variant={party.status === "notified" ? "default" : "secondary"}>
                        {party.status}
                      </Badge>
                    </div>
                  )) || (
                    <div className="text-sm text-muted-foreground">No parties configured</div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                {selectedInstance.status !== "completed" && selectedInstance.status !== "cancelled" && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => handleUpdateInstanceStatus(selectedInstance.id, "cancelled")}
                    >
                      Cancel Workflow
                    </Button>
                    <Button
                      onClick={() => handleUpdateInstanceStatus(selectedInstance.id, "completed")}
                    >
                      <CheckCircle className="h-4 w-4" />
                      Mark Complete
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
